<template>
  <div class="scenarios-tab">
    <!-- Screen Transitions Section -->
    <div class="section screen-transitions-section">
      <div class="section-header">
        <span class="section-title">Screen Transitions</span>
        <span class="current-state-badge" :class="currentState">{{ currentState }}</span>
      </div>

      <!-- State Buttons Grid -->
      <div class="state-buttons-grid">
        <button
          v-for="stateOption in stateOptions"
          :key="stateOption.id"
          class="state-btn"
          :class="{ active: currentState === stateOption.id }"
          @click="transitionToState(stateOption.id)"
        >
          <span class="state-icon">{{ stateOption.icon }}</span>
          <span class="state-name">{{ stateOption.label }}</span>
        </button>
      </div>

      <!-- Session Info (shown when session is active) -->
      <div v-if="machine.sessionId" class="session-info">
        <span class="session-label">Session:</span>
        <span class="session-id">{{ machine.sessionId }}</span>
      </div>
    </div>

    <!-- Mock Data Section -->
    <div class="section">
      <div class="section-header collapsible" @click="showMockData = !showMockData">
        <span class="section-title">Mock Data</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          width="14"
          height="14"
          class="chevron"
          :class="{ open: showMockData }"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </div>

      <div v-if="showMockData" class="mock-data-grid">
        <div class="config-row">
          <label class="config-label">Session ID</label>
          <input type="text"
class="config-input" v-model="mockData.sessionId"
placeholder="mock-session-123" />
        </div>

        <div class="config-row">
          <label class="config-label">Recorder Session</label>
          <input type="text"
class="config-input" v-model="mockData.recorderSessionId"
placeholder="rec-456" />
        </div>

        <div class="config-row">
          <label class="config-label">Recorder URL</label>
          <input
            type="text"
            class="config-input"
            v-model="mockData.recorderUrl"
            placeholder="https://recorder.example.com/..."
          />
        </div>

        <div class="config-row">
          <label class="config-label">Templates</label>
          <div class="config-select-group">
            <button
              v-for="count in [0, 1, 3, 5]"
              :key="count"
              class="config-option"
              :class="{ active: mockData.templateCount === count }"
              @click="mockData.templateCount = count"
            >
              {{ count }}
            </button>
          </div>
        </div>

        <div class="mock-actions">
          <button class="action-btn compact" @click="applyMockData">Apply Mock Data</button>
          <button class="action-btn compact" @click="resetMockData">Reset</button>
        </div>
      </div>
    </div>

    <!-- Quick Screen Presets -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">Screen Presets</span>
      </div>
      <div class="screen-presets-grid">
        <button
          v-for="preset in screenPresets"
          :key="preset.id"
          class="preset-card screen-preset"
          @click="applyScreenPreset(preset)"
        >
          <span class="preset-icon">{{ preset.icon }}</span>
          <span class="preset-name">{{ preset.name }}</span>
          <span class="preset-desc">{{ preset.description }}</span>
        </button>
      </div>
    </div>

    <!-- Quick Presets Section -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">Simulation Presets</span>
      </div>
      <div class="presets-grid">
        <button
          v-for="preset in presets"
          :key="preset.id"
          class="preset-card"
          :class="{ active: isPresetActive(preset) }"
          @click="applyPreset(preset)"
        >
          <span class="preset-icon">{{ preset.icon }}</span>
          <span class="preset-name">{{ preset.name }}</span>
          <span class="preset-desc">{{ preset.description }}</span>
        </button>
      </div>
    </div>

    <!-- Individual Toggles Section -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">Individual Controls</span>
      </div>

      <div class="toggle-list">
        <div class="toggle-item" :class="{ active: settings.simulateSocketDisconnect }">
          <div class="toggle-info">
            <span class="toggle-name">Socket Disconnect</span>
            <span class="toggle-desc">Disconnect socket 1s after "recorded" status</span>
          </div>
          <label class="switch">
            <input
              type="checkbox"
              :checked="settings.simulateSocketDisconnect"
              @change="toggleSetting('simulateSocketDisconnect')"
            />
            <span class="slider"></span>
          </label>
        </div>

        <div class="toggle-item" :class="{ active: settings.simulatePollingTimeout }">
          <div class="toggle-info">
            <span class="toggle-name">Polling Timeout</span>
            <span class="toggle-desc">Polling never succeeds, timeout reduced to 15s</span>
          </div>
          <label class="switch">
            <input
              type="checkbox"
              :checked="settings.simulatePollingTimeout"
              @change="toggleSetting('simulatePollingTimeout')"
            />
            <span class="slider"></span>
          </label>
        </div>

        <div class="toggle-item" :class="{ active: testConfig.autoStart }">
          <div class="toggle-info">
            <span class="toggle-name">Auto Start</span>
            <span class="toggle-desc">Automatically open the popover on load</span>
          </div>
          <label class="switch">
            <input
              type="checkbox"
              :checked="testConfig.autoStart"
              @change="updateConfig('autoStart', !testConfig.autoStart)"
            />
            <span class="slider"></span>
          </label>
        </div>
      </div>
    </div>

    <!-- Test Configuration Section -->
    <div class="section">
      <div class="section-header collapsible" @click="showTestConfig = !showTestConfig">
        <span class="section-title">Test Configuration</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          width="14"
          height="14"
          class="chevron"
          :class="{ open: showTestConfig }"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </div>

      <div v-if="showTestConfig" class="config-grid">
        <div class="config-row">
          <label class="config-label">Position</label>
          <div class="config-select-group">
            <button
              v-for="opt in POSITION_OPTIONS"
              :key="opt.value"
              class="config-option"
              :class="{ active: testConfig.position === opt.value }"
              @click="updateConfig('position', opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <div class="config-row">
          <label class="config-label">Token</label>
          <input
            type="text"
            class="config-input token-input"
            :value="testConfig.token"
            @input="updateConfig('token', ($event.target as HTMLInputElement).value)"
            placeholder="JWT Token"
          />
        </div>

        <div class="config-row">
          <label class="config-label">User ID</label>
          <input
            type="text"
            class="config-input"
            :value="testConfig.userId"
            @input="updateConfig('userId', ($event.target as HTMLInputElement).value)"
          />
        </div>

        <div class="config-row">
          <label class="config-label">License ID</label>
          <input
            type="text"
            class="config-input"
            :value="testConfig.licenseId"
            @input="updateConfig('licenseId', ($event.target as HTMLInputElement).value)"
          />
        </div>

        <div class="config-row">
          <label class="config-label">Patient ID</label>
          <input
            type="text"
            class="config-input"
            :value="testConfig.patientId"
            @input="updateConfig('patientId', ($event.target as HTMLInputElement).value)"
          />
        </div>

        <div class="config-row">
          <label class="config-label">PMS</label>
          <div class="config-select-group">
            <button
              v-for="opt in PMS_OPTIONS"
              :key="opt.value"
              class="config-option"
              :class="{ active: testConfig.pms === opt.value }"
              @click="updateConfig('pms', opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>

        <button class="action-btn compact" @click="resetTestConfig">Reset Config</button>
      </div>
    </div>

    <!-- Actions Section -->
    <div class="section">
      <div class="section-header">
        <span class="section-title">Actions</span>
      </div>
      <div class="actions-list">
        <button class="action-btn" @click="resetAllSettings">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14"
height="14">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          Reset All Settings
        </button>
        <button class="action-btn" @click="resetAIState">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14"
height="14">
            <circle cx="12" cy="12" r="10" />
            <path d="m15 9-6 6" />
            <path d="m9 9 6 6" />
          </svg>
          Reset AI State
        </button>
        <button class="action-btn danger" @click="clearStorageAndReload">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14"
height="14">
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
          Clear Storage &amp; Reload
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive } from "vue";
import { useDevSettings } from "@/composables/useDevSettings";
import { useTestConfig, POSITION_OPTIONS, PMS_OPTIONS } from "@/composables/useTestConfig";
import { useStateMachine, type AIAssistantState, type Template } from "@/composables/useStateMachine";

const { settings, updateSetting, resetSettings } = useDevSettings();
const { config: testConfig, updateConfig, resetConfig: resetTestConfig } = useTestConfig();

// AI Assistant State Machine (singleton)
const machine = useStateMachine();

// Current state from State Machine
const currentState = computed(() => machine.state);

// State options for quick transitions (matching useStateMachine states)
const stateOptions: { id: AIAssistantState; label: string; icon: string }[] = [
  { id: "idle", label: "Idle", icon: "⏸️" },
  { id: "form_template_selection", label: "Form Template", icon: "📋" },
  { id: "device_selection", label: "Device", icon: "📱" },
  { id: "recording", label: "Record", icon: "🎙️" },
  { id: "processing", label: "Process", icon: "⏳" },
  { id: "review_ready", label: "Review", icon: "✅" },
  { id: "error", label: "Error", icon: "❌" },
];

// Mock data state
const mockData = reactive({
  sessionId: "dev-session-" + Date.now().toString(36),
  recorderSessionId: "rec-" + Date.now().toString(36),
  recorderUrl: "https://recorder.noa.ai/dev-test",
  licenseId: "123",
  patientId: "456",
  templateCount: 3,
  summaryData:
    "## Anamnese\n\nPaciente apresenta queixa de dor de cabeça há 3 dias.\n\n## Exame Físico\n\nPA: 120/80 mmHg\nFC: 72 bpm\n\n## Conduta\n\nPrescrito analgésico.",
});

// Generate mock templates
const generateMockTemplates = (count: number): Template[] => {
  const templateNames = ["Consulta Geral", "Retorno", "Primeira Consulta", "Exame Físico", "Acompanhamento"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    description: templateNames[i] || `Template ${i + 1}`,
    sections: [],
  }));
};

// Screen presets for quick testing (using new flat state machine states)
interface ScreenPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  targetState: AIAssistantState;
  needsSessionData?: boolean;
  needsTemplates?: boolean;
  needsSummary?: boolean;
}

const screenPresets: ScreenPreset[] = [
  {
    id: "processing",
    name: "Processing",
    description: "Show processing state",
    icon: "⏳",
    targetState: "processing",
    needsSessionData: true,
  },
  {
    id: "review-ready",
    name: "Review Ready",
    description: "Show review with summary",
    icon: "✏️",
    targetState: "review_ready",
    needsSessionData: true,
    needsSummary: true,
  },
  {
    id: "recording",
    name: "Recording",
    description: "Show recorder modal",
    icon: "🎙️",
    targetState: "recording",
    needsSessionData: true,
  },
  {
    id: "templates",
    name: "Templates",
    description: "Show template selection",
    icon: "📋",
    targetState: "form_template_selection",
    needsTemplates: true,
  },
];

// Collapsible sections
const showTestConfig = ref(false);
const showMockData = ref(true);

/**
 * Create mock session data for testing
 */
const createMockSessionData = () => ({
  sessionId: mockData.sessionId,
  recorderSessionId: mockData.recorderSessionId,
  licenseId: mockData.licenseId,
  patientId: mockData.patientId,
  recorderUrl: mockData.recorderUrl,
});

/**
 * Transition to a specific state using the event-driven state machine.
 * This simulates the actual flow by sending appropriate events.
 */
const transitionToState = (newState: AIAssistantState) => {
  const currentState = machine.state;

  // First reset if needed to get to a clean state
  if (currentState !== "idle" && newState !== currentState) {
    // Save real templates before reset (reset clears them)
    const cachedTemplates = [...machine.templates];
    machine.reset();
    // Restore real templates if they existed
    if (cachedTemplates.length > 0) {
      machine.setTemplates(cachedTemplates);
    }
  }

  // Set up templates only if none are cached from the API
  if (machine.templates.length === 0 && mockData.templateCount > 0) {
    machine.setTemplates(generateMockTemplates(mockData.templateCount));
  }

  // Use events to transition through the state machine properly
  switch (newState) {
    case "idle":
      // Already reset above, nothing more needed
      break;

    case "form_template_selection":
      machine.send({ type: "FAB_CLICK" });
      break;

    case "device_selection":
      // Must go through template selection first
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      break;

    case "recording":
      // Go through template → device → recording
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      break;

    case "processing":
      // Go through template → device → recording → processing
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      machine.send({ type: "WS_RECORDED" });
      break;

    case "review_ready":
      // Full flow: template → device → recording → processing → review_ready
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      machine.send({ type: "WS_RECORDED" });
      machine.send({
        type: "WS_SUMMARIZED",
        summaryData: mockData.summaryData,
        transcriptionData: "Mock transcription data",
      });
      break;

    case "error":
      // Go to processing then trigger error
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      machine.send({ type: "WS_RECORDED" });
      machine.send({
        type: "WS_ERROR",
        error: "Simulated error for testing",
      });
      break;
  }
};

// Apply mock data to current session
const applyMockData = () => {
  machine.updateSessionData({
    sessionId: mockData.sessionId,
    recorderSessionId: mockData.recorderSessionId,
    licenseId: mockData.licenseId,
    patientId: mockData.patientId,
    summaryData: mockData.summaryData,
  });
  if (mockData.templateCount > 0) {
    machine.setTemplates(generateMockTemplates(mockData.templateCount));
  }
};

// Reset mock data to defaults
const resetMockData = () => {
  mockData.sessionId = "dev-session-" + Date.now().toString(36);
  mockData.recorderSessionId = "rec-" + Date.now().toString(36);
  mockData.recorderUrl = "https://recorder.noa.ai/dev-test";
  mockData.licenseId = "123";
  mockData.patientId = "456";
  mockData.templateCount = 3;
};

// Apply a screen preset
const applyScreenPreset = (preset: ScreenPreset) => {
  // Reset first
  machine.reset();

  // Set up templates if needed
  if (preset.needsTemplates || mockData.templateCount > 0) {
    machine.setTemplates(generateMockTemplates(mockData.templateCount || 3));
  }

  // Transition to the target state
  transitionToState(preset.targetState);
};

// Reset AI state machine
const resetAIState = () => {
  machine.reset();
};

interface Preset {
  id: string;
  name: string;
  description: string;
  icon: string;
  settings: {
    simulateSocketDisconnect: boolean;
    simulatePollingTimeout: boolean;
  };
}

const presets: Preset[] = [
  {
    id: "clean",
    name: "Clean Slate",
    description: "All simulations off",
    icon: "✨",
    settings: {
      simulateSocketDisconnect: false,
      simulatePollingTimeout: false,
    },
  },
  {
    id: "recovery",
    name: "Recovery Flow",
    description: "Test socket recovery",
    icon: "🔄",
    settings: {
      simulateSocketDisconnect: true,
      simulatePollingTimeout: false,
    },
  },
  {
    id: "timeout",
    name: "Timeout Test",
    description: "Test refresh fallback",
    icon: "⏱️",
    settings: {
      simulateSocketDisconnect: true,
      simulatePollingTimeout: true,
    },
  },
];

const isPresetActive = (preset: Preset) => {
  return (
    settings.value.simulateSocketDisconnect === preset.settings.simulateSocketDisconnect &&
    settings.value.simulatePollingTimeout === preset.settings.simulatePollingTimeout
  );
};

const applyPreset = (preset: Preset) => {
  updateSetting("simulateSocketDisconnect", preset.settings.simulateSocketDisconnect);
  updateSetting("simulatePollingTimeout", preset.settings.simulatePollingTimeout);
};

const toggleSetting = (key: "simulateSocketDisconnect" | "simulatePollingTimeout") => {
  updateSetting(key, !settings.value[key]);
};

const resetAllSettings = () => {
  resetSettings();
};

const clearStorageAndReload = () => {
  sessionStorage.clear();
  localStorage.removeItem("ai-assistant-dev-config");
  localStorage.removeItem("ai-assistant-dev-settings");
  window.location.reload();
};
</script>

<style scoped>
.scenarios-tab {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  min-height: 0; /* Critical for flex child scrolling */
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.scenarios-tab::-webkit-scrollbar {
  width: 6px;
}

.scenarios-tab::-webkit-scrollbar-track {
  background: transparent;
}

.scenarios-tab::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #8090a0;
}

/* Screen Transitions Section */
.screen-transitions-section {
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 8px;
  padding: 12px;
}

.current-state-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.current-state-badge.idle {
  background: rgba(128, 128, 128, 0.2);
  color: #9090a0;
}

.current-state-badge.form_template_selection,
.current-state-badge.device_selection {
  background: rgba(168, 85, 247, 0.2);
  color: #a78bfa;
}

.current-state-badge.recording {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.current-state-badge.processing {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}

.current-state-badge.review_ready {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.current-state-badge.error {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

/* State Buttons Grid */
.state-buttons-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

@media (min-width: 500px) {
  .state-buttons-grid {
    grid-template-columns: repeat(7, 1fr);
  }
}

.state-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 4px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.state-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.state-btn.active {
  background: rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.4);
}

.state-icon {
  font-size: 14px;
  line-height: 1;
}

.state-name {
  font-size: 9px;
  font-weight: 500;
  color: #b0b0c0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.state-btn.active .state-name {
  color: #60a5fa;
}

/* Session Info */
.session-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.session-label {
  font-size: 10px;
  color: #8090a0;
  white-space: nowrap;
}

.session-id {
  font-size: 10px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #60a5fa;
  background: rgba(59, 130, 246, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

/* Screen Presets Grid */
.screen-presets-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.screen-preset {
  border-color: rgba(59, 130, 246, 0.15);
}

.screen-preset:hover {
  border-color: rgba(59, 130, 246, 0.3);
  background: rgba(59, 130, 246, 0.1);
}

/* Mock Data Grid */
.mock-data-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.mock-actions {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.mock-actions .action-btn {
  flex: 1;
}

/* Presets Grid */
.presets-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.preset-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.preset-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
}

.preset-card.active {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
}

.preset-icon {
  font-size: 18px;
  line-height: 1;
}

.preset-name {
  font-size: 12px;
  font-weight: 600;
  color: #e0e0f0;
  text-align: center;
}

.preset-desc {
  font-size: 10px;
  color: #8090a0;
  text-align: center;
  line-height: 1.3;
}

/* Toggle List */
.toggle-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: all 0.15s ease;
}

.toggle-item.compact {
  padding: 8px 10px;
}

.toggle-item.active {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.2);
}

.toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-name {
  font-size: 13px;
  font-weight: 500;
  color: #e0e0f0;
}

.toggle-item.compact .toggle-name {
  font-size: 12px;
}

.toggle-desc {
  font-size: 11px;
  color: #8090a0;
}

.toggle-item.compact .toggle-desc {
  font-size: 10px;
}

/* Custom Switch */
.switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  transition: 0.2s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background: #808090;
  border-radius: 50%;
  transition: 0.2s;
}

input:checked + .slider {
  background: rgba(59, 130, 246, 0.5);
}

input:checked + .slider:before {
  transform: translateX(16px);
  background: #60a5fa;
}

/* Actions */
.actions-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #b0b0c0;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e0e0f0;
}

.action-btn.danger {
  border-color: rgba(239, 68, 68, 0.2);
}

.action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.action-btn.compact {
  padding: 8px 12px;
  font-size: 11px;
}

/* Collapsible Header */
.section-header.collapsible {
  cursor: pointer;
  user-select: none;
}

.section-header.collapsible:hover {
  opacity: 0.8;
}

.chevron {
  color: #707090;
  transition: transform 0.2s ease;
}

.chevron.open {
  transform: rotate(90deg);
}

/* Config Grid */
.config-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}

.config-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.config-label {
  font-size: 12px;
  color: #a0a0b0;
  min-width: 80px;
}

.config-input {
  flex: 1;
  padding: 6px 10px;
  font-size: 12px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #e0e0f0;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 5px;
  outline: none;
  transition: border-color 0.15s ease;
}

.config-input:focus {
  border-color: rgba(59, 130, 246, 0.4);
}

.config-input::placeholder {
  color: #606080;
}

.config-input.token-input {
  font-size: 10px;
  max-width: 180px;
  text-overflow: ellipsis;
}

.config-select-group {
  display: flex;
  gap: 4px;
}

.config-option {
  padding: 5px 10px;
  font-size: 11px;
  font-weight: 500;
  color: #9090a0;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.config-option:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #c0c0d0;
}

.config-option.active {
  background: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
  color: #60a5fa;
}
</style>
